"use client";

import { Input, Switch, Textarea } from "@/ui";
import ImageUpload from "./ImageUpload";
import DocumentUpload from "./DocumentUpload";
import SettingsField from "./SettingsField";
import isEmailValid from "@lib/helpers/isEmailValid";
import isValidUrl from "@lib/helpers/isValidUrl";
import isValidHttpUrl from "@lib/helpers/isValidHttpUrl";
import parseWidget from "@lib/helpers/parseWidget";
import TipTapEditor from "@components/TipTapEditor";
import { DASHBOARD_FOCUS } from "@lib/enums";
import styles from "./Recruiter.module.css";

export default function Recruiter({
  recruiter,
  formError,
  setRecruiter,
  setFormError,
  onFileUpload,
  mutation,
  settingsMutation,
  hasOutlookIntegration,
  showCompanyDescription,
}) {
  const isSubmissionsFocus =
    recruiter.dashboard_focus === DASHBOARD_FOCUS.SUBMISSIONS;

  const handleImageUpdate = (image) => {
    onFileUpload();
  };

  const handleEditorChange = (content) => {
    setRecruiter((prev) => ({ ...prev, email_signature_block: content }));
  };

  // Laravel returns an array of messages per field; the /ui controls take one.
  const errorFor = (field) => {
    const messages = formError[field];
    if (!messages) return undefined;
    return Array.isArray(messages) ? messages[0] : messages;
  };

  const setField = (field) => (event) =>
    setRecruiter((prev) => ({ ...prev, [field]: event.target.value }));

  const validateEmail = (email) => {
    const updatedFormError = { ...formError };
    if (!email || isEmailValid(email.trim())) {
      delete updatedFormError.reply_to_email;
      setFormError(updatedFormError);
      return;
    }

    const emailError = {
      ...formError,
      reply_to_email: ["Please enter a valid email address"],
    };
    setFormError(emailError);
  };

  const validateUrl = (field, label) => (event) => {
    const value = event.target.value;
    const updatedFormError = { ...formError };
    if (!value || isValidHttpUrl(value.trim())) {
      delete updatedFormError[field];
      setFormError(updatedFormError);
      return;
    }

    setFormError({
      ...formError,
      [field]: [
        `Please enter a valid ${label} starting with https:// (e.g. https://example.com)`,
      ],
    });
  };

  const validateWidget = (content) => {
    const updatedFormError = { ...formError };
    if (!content || isValidUrl(content.trim())) {
      delete updatedFormError.reviews_widget;
      setFormError(updatedFormError);
      return;
    }

    if (content.length < 50) {
      // assume url not widget
      const urlError = {
        ...formError,
        reviews_widget: ["Please enter a valid URL or widget code"],
      };
      setFormError(urlError);
      return;
    }

    try {
      parseWidget(content.trim());
      delete updatedFormError.reviews_widget;
      setFormError(updatedFormError);
    } catch (error) {
      const urlError = {
        ...formError,
        reviews_widget: ["Please enter a valid URL or widget code"],
      };
      setFormError(urlError);
    }
  };

  return (
    <>
      <SettingsField
        label="Business name"
        description="This name will appear at the top of your Interactive Float page."
        htmlFor="settings-company"
      >
        <Input
          id="settings-company"
          fullWidth
          autoFocus
          error={errorFor("company")}
          value={recruiter.company}
          onChange={setField("company")}
        />
      </SettingsField>

      <SettingsField
        label="Job title"
        description="What is your job title? This will appear on your Interactive Float page."
        htmlFor="settings-job-title"
      >
        <Input
          id="settings-job-title"
          fullWidth
          error={errorFor("job_title")}
          value={recruiter.job_title}
          onChange={setField("job_title")}
        />
      </SettingsField>

      {showCompanyDescription && (
        <SettingsField
          label="Company description"
          description="A short description of your company. This will appear on your Interactive Float page."
          htmlFor="settings-company-description"
        >
          <Textarea
            id="settings-company-description"
            rows={3}
            error={errorFor("company_description")}
            value={recruiter.company_description}
            onChange={(value) =>
              setRecruiter((prev) => ({ ...prev, company_description: value }))
            }
          />
        </SettingsField>
      )}

      <SettingsField
        label="Reply-To email address"
        description="Replies to Float emails will be sent to this address."
        htmlFor="settings-reply-to-email"
      >
        <Input
          id="settings-reply-to-email"
          fullWidth
          type="email"
          error={errorFor("reply_to_email")}
          value={recruiter.reply_to_email}
          onBlur={(event) => validateEmail(event.target.value)}
          onChange={setField("reply_to_email")}
          disabled={hasOutlookIntegration}
        />
        {hasOutlookIntegration && (
          <p className={styles.note}>
            Email address set via Outlook integration
          </p>
        )}
      </SettingsField>

      <SettingsField
        label="LinkedIn profile URL"
        description="Add your LinkedIn profile URL to allow Float recipients to connect with you."
        htmlFor="settings-linkedin-url"
      >
        <Input
          id="settings-linkedin-url"
          fullWidth
          error={errorFor("linkedin_url")}
          value={recruiter.linkedin_url}
          onBlur={validateUrl("linkedin_url", "LinkedIn URL")}
          onChange={setField("linkedin_url")}
        />
      </SettingsField>

      <SettingsField
        label="Website URL"
        description="Add your company website so that Float recipients can find out more."
        htmlFor="settings-website"
      >
        <Input
          id="settings-website"
          fullWidth
          error={errorFor("website")}
          value={recruiter.website}
          onBlur={validateUrl("website", "Website URL")}
          onChange={setField("website")}
        />
      </SettingsField>

      <SettingsField
        label="Instagram URL"
        description="Add your Instagram profile URL to allow Float recipients to follow you."
        htmlFor="settings-instagram"
      >
        <Input
          id="settings-instagram"
          fullWidth
          error={errorFor("instagram")}
          value={recruiter.instagram}
          onBlur={validateUrl("instagram", "Instagram URL")}
          onChange={setField("instagram")}
        />
      </SettingsField>

      <SettingsField
        label="Mobile"
        description="Add your mobile phone number so that Float recipients can contact you."
        htmlFor="settings-mobile-phone"
      >
        <Input
          id="settings-mobile-phone"
          fullWidth
          error={errorFor("mobile_phone")}
          value={recruiter.mobile_phone}
          onChange={setField("mobile_phone")}
        />
      </SettingsField>

      <SettingsField
        label="Calendar URL"
        description={
          <>
            This is the URL for the calendar service to allow Float recipients to
            book meetings. Floats recommends{" "}
            <a target="_blank" rel="noreferrer" href="https://calendly.com/">
              Calendly
            </a>
            .
          </>
        }
        htmlFor="settings-calendar-url"
      >
        <Input
          id="settings-calendar-url"
          fullWidth
          error={errorFor("calendar_url")}
          value={recruiter.calendar_url}
          onBlur={validateUrl("calendar_url", "Calendar URL")}
          onChange={setField("calendar_url")}
        />
      </SettingsField>

      <SettingsField
        label="Reviews Widget"
        description={
          <>
            Paste the{" "}
            <a
              href="https://help.sourcr.com/en/articles/3526526-how-do-i-set-up-my-review-widget"
              target="_blank"
              rel="noreferrer"
            >
              Sourcr
            </a>{" "}
            or{" "}
            <a
              href="https://www.google.com/search?q=google+review+widgets"
              target="_blank"
              rel="noreferrer"
            >
              Google
            </a>{" "}
            reviews widget code here to display it on your Interactive Float
            page. Only Sourcr and Google reviews are supported.
            <br />
            Widget code or URL are accepted.
          </>
        }
        htmlFor="settings-reviews-widget"
      >
        <Textarea
          id="settings-reviews-widget"
          rows={2}
          error={errorFor("reviews_widget")}
          value={recruiter.reviews_widget}
          onBlur={(event) => validateWidget(event.target.value)}
          onChange={(value) =>
            setRecruiter((prev) => ({ ...prev, reviews_widget: value }))
          }
        />
      </SettingsField>

      <div className={styles.imageRow}>
        <div className={styles.imageColumn}>
          <SettingsField
            label="Logo"
            description="This is the logo that will appear on the top of your Interactive Float page. For best results, use a square image, PNG format, and at least 300px by 300px."
          >
            <ImageUpload
              recruiterId={recruiter.id}
              image={{ logo: recruiter.logo_url }}
              updateImage={handleImageUpdate}
              setFormError={setFormError}
              mutation={mutation}
            />
          </SettingsField>
        </div>
        <div className={styles.imageColumn}>
          <SettingsField
            label="Your profile photo"
            description="This is the photo that will appear on the top of your Interactive Float page. For best results, use a square image, PNG or JPG format, and at least 500px by 500px."
          >
            <ImageUpload
              recruiterId={recruiter.id}
              image={{ photo: recruiter.photo_url }}
              updateImage={handleImageUpdate}
              setFormError={setFormError}
              mutation={mutation}
            />
          </SettingsField>
        </div>
      </div>

      <SettingsField
        label="Terms document"
        description="Upload your terms of business as a PDF or Word document (max 5MB). Once uploaded you can choose to attach it when sending a Float or a Submission."
      >
        <DocumentUpload
          field="terms_document"
          filename={recruiter.terms_document_filename}
          onUpdated={onFileUpload}
          mutation={settingsMutation}
          setFormError={setFormError}
        />
      </SettingsField>

      <SettingsField
        label="Email disclaimer"
        description="This is the content that appears below the generated float email footer. Typically used for disclaimers."
      >
        <TipTapEditor
          content={recruiter.email_signature_block || ""}
          onChange={handleEditorChange}
          placeholder="Enter your email disclaimer..."
          minHeight="100px"
        />
      </SettingsField>

      <SettingsField
        label="Dashboard analytics focus"
        description="Choose whether your dashboard analytics report on Floats or Submissions."
      >
        <div className={styles.focusToggle}>
          <span
            className={
              isSubmissionsFocus ? styles.focusOptionMuted : styles.focusOption
            }
          >
            Floats
          </span>
          <Switch
            aria-label="Dashboard analytics focus"
            checked={isSubmissionsFocus}
            onChange={(checked) =>
              setRecruiter((prev) => ({
                ...prev,
                dashboard_focus: checked
                  ? DASHBOARD_FOCUS.SUBMISSIONS
                  : DASHBOARD_FOCUS.FLOATS,
              }))
            }
          />
          <span
            className={
              isSubmissionsFocus ? styles.focusOption : styles.focusOptionMuted
            }
          >
            Submissions
          </span>
        </div>
      </SettingsField>
    </>
  );
}
